import type { JourneyNode } from "@/lib/journey-api";
import type { JourneyPlanStep } from "@/lib/journey-bindings";
import {
  countReflowUpdates,
  reflowPlannedAts,
  shanghaiWallToIso,
} from "@/lib/plan-schedule-reflow";
import { insertPlannedAt } from "@/lib/timeline-zones";

const SHANGHAI_TZ = "Asia/Shanghai";

export const DEFAULT_SEND_SLOTS = ["10:00", "14:00", "18:00"];

export const SEND_NOW_HINT = "马上创建企微群发任务，需员工在群发助手确认。站内不代发。";

export const SCHEDULE_HINT = "只改计划时间，不推企微。到点由档点自动编排（须已成稿）。";

export const SCHEDULE_INSERT_SOURCE_HINT =
  "只改计划时间。这条是再次安排的步骤，到点不会自动出站，需要再到时候点立刻发送。";

export const PAST_TIME_HINT = "请选择晚于现在的时间";

export type SendPlanStepMode = "now" | "schedule";

/** 上海墙钟 date + time 拆分（秒忽略） */
export function shanghaiWallParts(d: Date): { date: string; time: string } {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: SHANGHAI_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  const parts = fmt.formatToParts(d);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? "";
  return {
    date: `${get("year")}-${get("month")}-${get("day")}`,
    time: `${get("hour").padStart(2, "0")}:${get("minute").padStart(2, "0")}`,
  };
}

/** `<input type="time">` 可能带秒，统一成 HH:MM */
export function normalizeWallTime(time: string): string | null {
  const m = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(time.trim());
  if (!m) return null;
  return `${m[1]!.padStart(2, "0")}:${m[2]}`;
}

/** 切到定时时的默认日+时分：insertPlannedAt 下一档，且必须严格 > now */
export function defaultScheduleWall(
  now: Date,
  slots: string[] = DEFAULT_SEND_SLOTS,
): { date: string; time: string } {
  const resolved = slots.length > 0 ? slots : DEFAULT_SEND_SLOTS;
  let wall = shanghaiWallParts(insertPlannedAt(now, resolved));
  let guard = 0;
  while (!isScheduleInFuture(wall.date, wall.time, now) && guard < 8) {
    wall = bumpToNextSlot(wall.date, wall.time, resolved);
    guard += 1;
  }
  return wall;
}

export function isScheduleInFuture(date: string, time: string, now: Date): boolean {
  const hhmm = normalizeWallTime(time);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !hhmm) return false;
  const ms = new Date(shanghaiWallToIso(date, hhmm)).getTime();
  if (Number.isNaN(ms)) return false;
  return ms > now.getTime();
}

/** 所选日该档 ≤ now → 快捷按钮视觉 muted（仍可点，点了会跳次日） */
export function isSlotPastOnDate(date: string, slot: string, now: Date): boolean {
  const hhmm = normalizeWallTime(slot);
  if (!hhmm) return false;
  const ms = new Date(shanghaiWallToIso(date, hhmm)).getTime();
  if (Number.isNaN(ms)) return false;
  return ms <= now.getTime();
}

function bumpToNextSlot(
  date: string,
  time: string,
  slots: string[],
): { date: string; time: string } {
  const sorted = [...slots].sort();
  const later = sorted.find((s) => s > time);
  if (later) return { date, time: later };
  return { date: addCalendarDays(date, 1), time: sorted[0] ?? "10:00" };
}

function addCalendarDays(dateStr: string, days: number): string {
  const [y, m, d] = dateStr.split("-").map(Number);
  const utc = new Date(Date.UTC(y, (m ?? 1) - 1, (d ?? 1) + days));
  const yy = utc.getUTCFullYear();
  const mm = String(utc.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(utc.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

export function reflowFollowupCopy(n: number): string {
  if (n <= 0) return "没有后续待办需要改期。";
  return `后续 ${n} 步会按手册间隔改日期，尽量保留原来的几点。若会撞车或早于现在，再往后顺延。`;
}

export function scheduleModeHint(insertSource: string | null | undefined): string {
  return insertSource ? SCHEDULE_INSERT_SOURCE_HINT : SCHEDULE_HINT;
}

export function confirmButtonLabel(mode: SendPlanStepMode, confirming: boolean): string {
  if (confirming) return mode === "now" ? "发送中…" : "改期中…";
  return mode === "now" ? "确认发送" : "确认改期";
}

export function countScheduleReflows(opts: {
  now: Date;
  currentStepId: number | null;
  selectedDate: string;
  selectedTime: string;
  steps: JourneyPlanStep[];
  nodes: Pick<JourneyNode, "id" | "offset_days">[];
  inFlightStepIds: number[];
  sentStepIds: number[];
}): number {
  if (opts.currentStepId == null) return 0;
  const hhmm = normalizeWallTime(opts.selectedTime);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(opts.selectedDate) || !hhmm) {
    return 0;
  }
  const selectedAt = new Date(shanghaiWallToIso(opts.selectedDate, hhmm));
  if (Number.isNaN(selectedAt.getTime())) return 0;
  return countReflowUpdates(
    reflowPlannedAts({
      now: opts.now,
      currentStepId: opts.currentStepId,
      selectedAt,
      steps: opts.steps,
      nodes: opts.nodes,
      inFlightStepIds: opts.inFlightStepIds,
      sentStepIds: opts.sentStepIds,
    }),
  );
}
